/**
 * Authenticated transport envelopes — NET-REL-001 / SEC-1-4.
 *
 * Events are NIP-01 compliant so they are accepted by ANY Nostr relay
 * (private AUTH relays and public community fallbacks alike — NET-REL-006):
 * - `pubkey` = sender BIP-340 x-only transport (secp256k1) key;
 * - `id`     = sha256 of the canonical serialization below (verified by relays);
 * - `sig`    = BIP-340 Schnorr signature over the 32-byte id, produced by the
 *   Rust core (`WasmIdentity.transportSign`) — the secret never leaves the core.
 *
 * Routing uses the `w` tag (16-hex messaging-key prefix of the RECIPIENT).
 * NIP-01 relays validate `p` tags as 64-hex pubkeys (fixed-size), so the
 * metadata-minimized hint must live on a non-validated single-letter tag.
 */

export type UnsignedTransportEvent = {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
};

export type SignedTransportEvent = UnsignedTransportEvent & { sig: string };

/** NIP-01 canonical encoding — `id` is sha256 of exactly these bytes. */
export function canonicalTransportEventBytes(ev: UnsignedTransportEvent): Uint8Array {
  const payload = JSON.stringify([
    0,
    ev.pubkey.toLowerCase(),
    ev.created_at,
    ev.kind,
    ev.tags,
    ev.content,
  ]);
  return new TextEncoder().encode(payload);
}

/** SHA-256 over bytes — BufferSource-safe for TS DOM types. */
export async function sha256Bytes(data: Uint8Array): Promise<Uint8Array> {
  const buf = data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer;
  return new Uint8Array(await crypto.subtle.digest("SHA-256", buf));
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function hexToBytes(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * Build a NIP-01-valid signed transport event. `signTransport` receives the
 * 32-byte event id and must return the 64-byte BIP-340 signature (Rust core).
 * `createdAt` is supplied by callers so outbox retries recompute the SAME
 * id/sig (stable wire id — dedupe stays exact).
 */
export async function buildTransportEvent(opts: {
  /** Sender x-only transport key (64 hex, from WasmIdentity.transportPublicKey). */
  secpPubkeyHex: string;
  /** Unix seconds — keep stable across retries for a stable wire id. */
  createdAt: number;
  kind: number;
  tags: string[][];
  content: string;
  signTransport: (idBytes: Uint8Array) => Uint8Array;
}): Promise<SignedTransportEvent> {
  const unsigned = {
    id: "",
    pubkey: opts.secpPubkeyHex.toLowerCase(),
    created_at: Math.floor(opts.createdAt / 1000),
    kind: opts.kind,
    tags: opts.tags,
    content: opts.content,
  };
  const digest = await sha256Bytes(canonicalTransportEventBytes(unsigned));
  const id = bytesToHex(digest);
  const sig = bytesToHex(opts.signTransport(hexToBytes(id)));
  return { ...unsigned, id, sig };
}

/** Recipient routing hint — first 16 hex of the messaging public key (NET-REL-002). */
export function transportRoutingHint(messagingPublicKeyHex: string): string {
  return messagingPublicKeyHex.replace(/\s+/g, "").toLowerCase().slice(0, 16);
}

export function isDevPlaceholderSig(sig: string | undefined): boolean {
  return !sig || sig === "dev" || sig === "unsigned";
}

// ---------------------------------------------------------------------------
// Protocol v2 sealed events (NIP-59-style throwaway signing key)
// ---------------------------------------------------------------------------

/** Signs one event id with a key used for nothing else (never the identity). */
export type EventSigner = {
  pubkeyHex: string;
  sign: (id32: Uint8Array) => Uint8Array;
};

/** Base64 without the call-stack limits of `String.fromCharCode(...bytes)`. */
export function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Protocol-v2 envelope version byte (first byte of the decoded content). */
export const SEALED_ENVELOPE_VERSION = 2;
/** version(1) ‖ key id(8) ‖ X25519(32) ‖ ML-KEM ct(1088) ‖ AEAD tag(16). */
const SEALED_ENVELOPE_MIN = 1 + 8 + 32 + 1088 + 16;
/** Default relay retention hint for undelivered messages (NET-REL-004). */
export const SEALED_EVENT_TTL_DAYS = 30;

/**
 * Build a kind-1059 event whose content is an opaque v2 envelope. Relays see a
 * throwaway pubkey, the recipient routing hint, created_at and a bucketed size.
 * `createdAtMs` must be stable for a given outbox item (id/sig are stored and
 * republished verbatim, never rebuilt).
 */
export async function buildSealedTransportEvent(opts: {
  envelope: Uint8Array;
  recipientHint: string;
  /**
   * Recipient transport x-only key (64 hex) → standard NIP-17 `p` inbox tag, so
   * gift-wrap-aware relays (NIP-42 gated) serve it to the authenticated recipient.
   */
  recipientPubkeyHex?: string;
  createdAtMs: number;
  signer: EventSigner;
  ttlDays?: number;
}): Promise<SignedTransportEvent> {
  const createdAt = Math.floor(opts.createdAtMs / 1000);
  const ttl = Math.floor((opts.ttlDays ?? SEALED_EVENT_TTL_DAYS) * 86400);
  const p = opts.recipientPubkeyHex?.toLowerCase();
  const tags: string[][] = [];
  if (p && /^[0-9a-f]{64}$/.test(p)) tags.push(["p", p]);
  tags.push(["w", opts.recipientHint.toLowerCase()], ["expiration", String(createdAt + ttl)]);
  const unsigned = {
    id: "",
    pubkey: opts.signer.pubkeyHex.toLowerCase(),
    created_at: createdAt,
    kind: 1059,
    tags,
    content: bytesToBase64(opts.envelope),
  };
  const id = bytesToHex(await sha256Bytes(canonicalTransportEventBytes(unsigned)));
  const sig = bytesToHex(opts.signer.sign(hexToBytes(id)));
  return { ...unsigned, id, sig };
}

/**
 * Extract a v2 envelope from event content, or null when the content is not a
 * v2 envelope (v1 JSON envelopes, garbage, truncated data).
 */
export function envelopeFromEventContent(content: string): Uint8Array | null {
  if (!content || content.length < 64 || !/^[A-Za-z0-9+/]+={0,2}$/.test(content)) return null;
  let bytes: Uint8Array;
  try {
    bytes = base64ToBytes(content);
  } catch {
    return null;
  }
  if (bytes.length < SEALED_ENVELOPE_MIN || bytes[0] !== SEALED_ENVELOPE_VERSION) return null;
  return bytes;
}

/** True for protocol-v1 cleartext JSON envelopes (retired; never processed). */
export function isLegacyV1Content(content: string): boolean {
  if (!content || content[0] !== "{") return false;
  try {
    const o = JSON.parse(content) as Record<string, unknown>;
    return typeof o.sealedJson === "string" && typeof o.conversationId === "string";
  } catch {
    return false;
  }
}
